/**
 * Client API pour la récupération de fiches réglementaires Polyprint FDS.
 *
 * Tous les appels passent par le backend LabelForge (jamais directement
 * vers fds.polyprint-studio.fr) parce que :
 *   1. Le token utilisateur est chiffré côté backend et ne doit JAMAIS
 *      transiter par le navigateur en clair après sa saisie initiale.
 *   2. Le rate-limiting et l'audit log sont centralisés côté backend.
 *
 * Endpoints proxy :
 *   GET    /api/me/regulatory-token         → état (masqué)
 *   PUT    /api/me/regulatory-token         → enregistre
 *   DELETE /api/me/regulatory-token         → supprime
 *   POST   /api/me/regulatory-token/test    → ping API FDS
 *   GET    /api/regulatory/{ufi}            → relais vers l'API FDS
 *   GET    /api/regulatory/catalog/clients      → liste des clients (légère)
 *   GET    /api/regulatory/catalog/clients/{id} → un client, gammes et produits compris
 */
import { API_URL, messageDErreur } from './client';
import { useAuthStore } from '../store/useAuthStore';


async function authFetch(path, options = {}) {
  const { authFetch: af } = useAuthStore.getState();
  const res = await af(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    body: options.body && typeof options.body === 'object' ? JSON.stringify(options.body) : options.body,
  });
  if (!res.ok) {
    let detail = `Erreur ${res.status}`;
    // `j.detail` n'est PAS toujours une chaîne : un refus de validation Pydantic
    // sert un tableau d'objets, et `new Error(tableau)` donnait « [object Object] »
    // à l'écran — le motif du refus, lui, disparaissait. Signalé le 2026-09-10 en
    // enregistrant un jeton FDS.
    try { const j = await res.json(); if (j.detail) detail = messageDErreur(j.detail, detail); }
    catch (_) { /* not json */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}


/** Renvoie l'état actuel du token (configured: bool, masked: string|null). */
export async function getTokenStatus() {
  return authFetch('/api/me/regulatory-token');
}


/** Enregistre / remplace le token API FDS. */
export async function setToken(token) {
  return authFetch('/api/me/regulatory-token', {
    method: 'PUT',
    body: { token },
  });
}


/** Supprime le token API FDS. */
export async function deleteToken() {
  return authFetch('/api/me/regulatory-token', { method: 'DELETE' });
}


/** Teste la connexion à l'API FDS avec le token enregistré. */
export async function testToken() {
  return authFetch('/api/me/regulatory-token/test', { method: 'POST' });
}


/** Récupère la fiche réglementaire d'un UFI. */
export async function fetchRegulatoryByUfi(ufi) {
  const clean = encodeURIComponent(ufi.trim().toUpperCase());
  return authFetch(`/api/regulatory/${clean}`);
}


/**
 * Récupère l'arborescence complète clients → gammes → produits finis.
 * Le payload peut peser plusieurs Mo selon le compte ; on appelle ce
 * endpoint uniquement à l'ouverture du picker, pas en boucle.
 */
export async function fetchCatalog() {
  return authFetch('/api/regulatory/catalog');
}


/**
 * Liste des clients du périmètre du jeton — SANS les produits.
 * `{ clients: [{ id, name, country_code, produits_count }] }`. Quelques Ko.
 */
export async function fetchCatalogClients() {
  return authFetch('/api/regulatory/catalog/clients');
}


/**
 * Un client avec ses gammes et ses produits finis, étiquettes comprises :
 * aucun troisième appel n'est nécessaire pour importer. Appelé au dépliage du
 * client dans l'arbre, jamais en boucle — FDS limite à 30 appels par minute.
 *
 * Un 404 signifie que la liste des clients est périmée : l'appelant la recharge.
 */
export async function fetchCatalogClient(clientId) {
  return authFetch(`/api/regulatory/catalog/clients/${encodeURIComponent(clientId)}`);
}
